package form

import calculator.CalculatorInput

data class CalculatorFormState(
    val values: Map<String, String>,
) {
    fun value(key: String): String = values[key].orEmpty()

    fun update(key: String, value: String): CalculatorFormState {
        return copy(values = values + (key to value))
    }

    companion object {
        fun default(): CalculatorFormState {
            return CalculatorFormState(
                calculatorFieldDefinitions.associate { field ->
                    field.key to formatInput(field.defaultValue)
                }
            )
        }
    }
}

fun CalculatorFormState.Companion.fromInput(input: CalculatorInput): CalculatorFormState {
    return CalculatorFormState.default()
        .update("monthlyIncomeRub", formatInput(input.monthlyIncomeRub))
        .update("workDaysPerMonth", formatInput(input.workDaysPerMonth))
        .update("years", formatInput(input.years))
        .update("daysPerYear", formatInput(input.daysPerYear))
        .update("workHoursPerDay", formatInput(input.workHoursPerDay))
        .update("taxAndOpportunityCostFactor", formatInput(input.taxAndOpportunityCostFactor))
        .update("vndPerThousandRub", formatInput(input.vndPerThousandRub))
        .update("apecCardCostRub", formatInput(input.apecCardCostRub))
        .update("annualInflationPercent", formatInput(input.annualInflationPercent))
        .update("incomeGrowthPercent", formatInput(input.incomeGrowthPercent))
        .update("exchangeRateChangePercent", formatInput(input.exchangeRateChangePercent))
        .update("bufferDaysBeforeExit", formatInput(input.bufferDaysBeforeExit))
        .update("noEVisaRunCostVnd", formatInput(input.noEVisaRunCostVnd))
        .update("eVisaRunCostVnd", formatInput(input.eVisaRunCostVnd))
        .update("apecRunCostVnd", formatInput(input.apecRunCostVnd))
        .update("noEVisaLodgingCostVndPerRun", formatInput(input.noEVisaLodgingCostVndPerRun))
        .update("eVisaLodgingCostVndPerRun", formatInput(input.eVisaLodgingCostVndPerRun))
        .update("apecLodgingCostVndPerRun", formatInput(input.apecLodgingCostVndPerRun))
        .update("noEVisaPeriodDays", formatInput(input.noEVisaPeriodDays))
        .update("noEVisaStampChangeAfterYears", formatInput(input.noEVisaStampChangeAfterYears))
        .update("noEVisaChangedPeriodDays", formatInput(input.noEVisaChangedPeriodDays))
        .update("eVisaPeriodDays", formatInput(input.eVisaPeriodDays))
        .update("apecPeriodDays", formatInput(input.apecPeriodDays))
        .update("noEVisaLostWorkDaysPerRun", formatInput(input.noEVisaLostWorkDaysPerRun))
        .update("eVisaLostWorkDaysPerRun", formatInput(input.eVisaLostWorkDaysPerRun))
        .update("apecLostWorkDaysPerRun", formatInput(input.apecLostWorkDaysPerRun))
        .update("noEVisaExtraRiskDaysPerRun", formatInput(input.noEVisaExtraRiskDaysPerRun))
        .update("eVisaExtraRiskDaysPerRun", formatInput(input.eVisaExtraRiskDaysPerRun))
        .update("apecExtraRiskDaysPerRun", formatInput(input.apecExtraRiskDaysPerRun))
        .update("noEVisaAdminHoursPerRun", formatInput(input.noEVisaAdminHoursPerRun))
        .update("eVisaAdminHoursPerRun", formatInput(input.eVisaAdminHoursPerRun))
        .update("apecAdminHoursPerRun", formatInput(input.apecAdminHoursPerRun))
        .update("noEVisaDiscomfortRubPerRun", formatInput(input.noEVisaDiscomfortRubPerRun))
        .update("eVisaDiscomfortRubPerRun", formatInput(input.eVisaDiscomfortRubPerRun))
        .update("apecDiscomfortRubPerRun", formatInput(input.apecDiscomfortRubPerRun))
        .update("noEVisaRiskProbabilityPercent", formatInput(input.noEVisaRiskProbabilityPercent))
        .update("eVisaRiskProbabilityPercent", formatInput(input.eVisaRiskProbabilityPercent))
        .update("apecRiskProbabilityPercent", formatInput(input.apecRiskProbabilityPercent))
        .update("noEVisaRiskEventCostRub", formatInput(input.noEVisaRiskEventCostRub))
        .update("eVisaRiskEventCostRub", formatInput(input.eVisaRiskEventCostRub))
        .update("apecRiskEventCostRub", formatInput(input.apecRiskEventCostRub))
        .update("fastTrackCrossingsPerYear", formatInput(input.fastTrackCrossingsPerYear))
        .update("fastTrackMinutesSavedPerCrossing", formatInput(input.fastTrackMinutesSavedPerCrossing))
        .update("apecOtherTravelSavingsRub", formatInput(input.apecOtherTravelSavingsRub))
}

fun CalculatorFormState.toCalculatorInputOrErrors(): ParsedCalculatorInput {
    val parsed = mutableMapOf<String, Double>()
    val errors = mutableListOf<FieldError>()

    calculatorFieldDefinitions.forEach { field ->
        val raw = value(field.key)
        val value = if (raw.isBlank()) field.defaultValue else parseLocalizedDouble(raw)
        if (value == null) {
            errors += FieldError(field.key, "Введите число")
        } else if (!value.isFinite()) {
            errors += FieldError(field.key, "Введите конечное число")
        } else {
            if (field.minValue != null && value < field.minValue) {
                errors += FieldError(field.key, "Минимум ${formatInput(field.minValue)}")
            }
            if (field.maxValue != null && value > field.maxValue) {
                errors += FieldError(field.key, "Максимум ${formatInput(field.maxValue)}")
            }
            parsed[field.key] = value
        }
    }

    if (errors.isEmpty()) {
        val bufferDaysBeforeExit = parsed.getValue("bufferDaysBeforeExit")
        val stayPeriods = listOf(
            parsed.getValue("noEVisaPeriodDays"),
            parsed.getValue("noEVisaChangedPeriodDays"),
            parsed.getValue("eVisaPeriodDays"),
            parsed.getValue("apecPeriodDays"),
        )
        if (stayPeriods.any { bufferDaysBeforeExit >= it }) {
            errors += FieldError(
                "bufferDaysBeforeExit",
                "Запас дней должен быть меньше каждого срока пребывания",
            )
        }
    }

    if (errors.isNotEmpty()) {
        return ParsedCalculatorInput(input = null, errors = errors)
    }

    return ParsedCalculatorInput(
        input = CalculatorInput(
            monthlyIncomeRub = parsed.getValue("monthlyIncomeRub"),
            workDaysPerMonth = parsed.getValue("workDaysPerMonth"),
            vndPerThousandRub = parsed.getValue("vndPerThousandRub"),
            apecCardCostRub = parsed.getValue("apecCardCostRub"),
            annualInflationPercent = parsed.getValue("annualInflationPercent"),
            incomeGrowthPercent = parsed.getValue("incomeGrowthPercent"),
            exchangeRateChangePercent = parsed.getValue("exchangeRateChangePercent"),
            years = parsed.getValue("years"),
            daysPerYear = parsed.getValue("daysPerYear"),
            taxAndOpportunityCostFactor = parsed.getValue("taxAndOpportunityCostFactor"),
            bufferDaysBeforeExit = parsed.getValue("bufferDaysBeforeExit"),
            noEVisaRunCostVnd = parsed.getValue("noEVisaRunCostVnd"),
            eVisaRunCostVnd = parsed.getValue("eVisaRunCostVnd"),
            apecRunCostVnd = parsed.getValue("apecRunCostVnd"),
            noEVisaLodgingCostVndPerRun = parsed.getValue("noEVisaLodgingCostVndPerRun"),
            eVisaLodgingCostVndPerRun = parsed.getValue("eVisaLodgingCostVndPerRun"),
            apecLodgingCostVndPerRun = parsed.getValue("apecLodgingCostVndPerRun"),
            noEVisaPeriodDays = parsed.getValue("noEVisaPeriodDays"),
            noEVisaStampChangeAfterYears = parsed.getValue("noEVisaStampChangeAfterYears"),
            noEVisaChangedPeriodDays = parsed.getValue("noEVisaChangedPeriodDays"),
            eVisaPeriodDays = parsed.getValue("eVisaPeriodDays"),
            apecPeriodDays = parsed.getValue("apecPeriodDays"),
            noEVisaLostWorkDaysPerRun = parsed.getValue("noEVisaLostWorkDaysPerRun"),
            eVisaLostWorkDaysPerRun = parsed.getValue("eVisaLostWorkDaysPerRun"),
            apecLostWorkDaysPerRun = parsed.getValue("apecLostWorkDaysPerRun"),
            noEVisaExtraRiskDaysPerRun = parsed.getValue("noEVisaExtraRiskDaysPerRun"),
            eVisaExtraRiskDaysPerRun = parsed.getValue("eVisaExtraRiskDaysPerRun"),
            apecExtraRiskDaysPerRun = parsed.getValue("apecExtraRiskDaysPerRun"),
            noEVisaAdminHoursPerRun = parsed.getValue("noEVisaAdminHoursPerRun"),
            eVisaAdminHoursPerRun = parsed.getValue("eVisaAdminHoursPerRun"),
            apecAdminHoursPerRun = parsed.getValue("apecAdminHoursPerRun"),
            noEVisaDiscomfortRubPerRun = parsed.getValue("noEVisaDiscomfortRubPerRun"),
            eVisaDiscomfortRubPerRun = parsed.getValue("eVisaDiscomfortRubPerRun"),
            apecDiscomfortRubPerRun = parsed.getValue("apecDiscomfortRubPerRun"),
            noEVisaRiskProbabilityPercent = parsed.getValue("noEVisaRiskProbabilityPercent"),
            eVisaRiskProbabilityPercent = parsed.getValue("eVisaRiskProbabilityPercent"),
            apecRiskProbabilityPercent = parsed.getValue("apecRiskProbabilityPercent"),
            noEVisaRiskEventCostRub = parsed.getValue("noEVisaRiskEventCostRub"),
            eVisaRiskEventCostRub = parsed.getValue("eVisaRiskEventCostRub"),
            apecRiskEventCostRub = parsed.getValue("apecRiskEventCostRub"),
            fastTrackCrossingsPerYear = parsed.getValue("fastTrackCrossingsPerYear"),
            fastTrackMinutesSavedPerCrossing = parsed.getValue("fastTrackMinutesSavedPerCrossing"),
            apecOtherTravelSavingsRub = parsed.getValue("apecOtherTravelSavingsRub"),
            workHoursPerDay = parsed.getValue("workHoursPerDay"),
        ),
        errors = emptyList(),
    )
}
